import { ReactNode } from "react";

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  width?: string;
}

export default function DataTable<T extends Record<string, any>>({
  columns,
  rows,
  onRowClick,
  rowKey,
  emptyHint = "尚無資料",
  selectable,
  getRowId,
  selectedIds,
  onToggleRow,
  onToggleAll,
}: {
  columns: Column<T>[];
  rows: T[];
  onRowClick?: (row: T) => void;
  /** 自訂 row key 取法。預設先試 line_user_id / order_id / id,fallback index */
  rowKey?: (row: T, i: number) => string | number;
  emptyHint?: string;
  /** 開啟 checkbox 多選欄;getRowId/selectedIds/onToggleRow/onToggleAll 須一起帶 */
  selectable?: boolean;
  getRowId?: (row: T) => string;
  selectedIds?: Set<string>;
  onToggleRow?: (id: string) => void;
  onToggleAll?: () => void;
}) {
  const defaultKey = (row: T, i: number) =>
    row.line_user_id || row.order_id || row.id || i;
  if (!rows.length) {
    return <div className="text-center py-12 text-muted text-sm">{emptyHint}</div>;
  }
  const sel = selectedIds || new Set<string>();
  const allChecked = selectable && getRowId
    ? rows.length > 0 && rows.every((r) => sel.has(getRowId(r)))
    : false;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-line/40 text-muted">
          <tr>
            {selectable && (
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={() => onToggleAll?.()}
                  className="cursor-pointer"
                />
              </th>
            )}
            {columns.map((c) => (
              <th
                key={c.key}
                className="text-left font-medium px-4 py-3"
                style={c.width ? { width: c.width } : undefined}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const id = selectable && getRowId ? getRowId(row) : "";
            return (
              <tr
                key={(rowKey || defaultKey)(row, i)}
                className={`border-t border-line ${onRowClick ? "cursor-pointer hover:bg-cream/60" : ""} ${sel.has(id) ? "bg-sage-light/30" : ""}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {selectable && (
                  <td
                    className="px-4 py-3 align-middle"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleRow?.(id);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={sel.has(id)}
                      onChange={() => {}}
                      className="cursor-pointer"
                    />
                  </td>
                )}
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 align-middle">
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
